import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Calendar, LogOut, Plus, Trash2, CheckCircle, XCircle, CalendarDays, List, Edit, Save, Phone, Settings2, FileText } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CalendarView from "@/components/CalendarView";
import EnhancedAvailability from "@/components/EnhancedAvailability";
import DocumentManager from "@/components/DocumentManager";
import { formatTimeRange } from "@/utils/timeFormat";

const InterpreterDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout, setUser } = useContext(AuthContext);
  const [availability, setAvailability] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showAddSlot, setShowAddSlot] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: "",
    phone: "",
    languages: "",
    specializations: "",
    court_certifications: "",
  });
  const [newSlot, setNewSlot] = useState({
    date: "",
    start_time: "",
    end_time: "",
  });
  
  // Document manager state
  const [selectedBookingForDocs, setSelectedBookingForDocs] = useState(null);

  useEffect(() => {
    if (user) {
      fetchAvailability();
      fetchBookings();
      // Initialize profile data
      setProfileData({
        full_name: user.full_name || "",
        phone: user.phone || "",
        languages: user.languages?.join(", ") || "",
        specializations: user.specializations?.join(", ") || "",
        court_certifications: user.court_certifications?.join(", ") || "",
      });
    }
  }, [user]);

  const fetchAvailability = async () => {
    try {
      const { ok, data } = await apiRequest(`${API}/availability/interpreter/${user.user_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (ok) {
        setAvailability(data);
      }
    } catch (error) {
      console.error("Failed to fetch availability:", error);
    }
  };

  const fetchBookings = async () => {
    try {
      const { ok, data } = await apiRequest(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (ok) {
        setBookings(data);
      }
    } catch (error) {
      console.error("Failed to fetch bookings:", error);
    }
  };

  // Use XMLHttpRequest to avoid body stream issues
  const apiRequest = (url, options = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(options.method || 'GET', url, true);
      if (options.headers) {
        Object.keys(options.headers).forEach(key => {
          xhr.setRequestHeader(key, options.headers[key]);
        });
      }
      xhr.timeout = 10000;
      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
        } catch (e) {
          resolve({ ok: false, status: xhr.status, data: { detail: 'Invalid server response' } });
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Request timed out'));
      xhr.send(options.body || null);
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setEditingProfile(true);

    try {
      const updatePayload = {
        full_name: profileData.full_name,
        phone: profileData.phone || "",
        languages: profileData.languages ? profileData.languages.split(",").map(l => l.trim()).filter(l => l) : [],
        specializations: profileData.specializations ? profileData.specializations.split(",").map(s => s.trim()).filter(s => s) : [],
        court_certifications: profileData.court_certifications ? profileData.court_certifications.split(",").map(c => c.trim()).filter(c => c) : [],
      };

      const { ok, data } = await apiRequest(`${API}/profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatePayload),
      });

      if (ok) {
        setUser(data);
        setShowEditProfile(false);
        toast.success("Profile updated successfully!");
      } else {
        toast.error(data.detail || "Failed to update profile");
      }
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error(error.message || "An error occurred. Please try again.");
    } finally {
      setEditingProfile(false);
    }
  };

  const handleAddSlot = async (e) => {
    e.preventDefault();
    try {
      const { ok, data } = await apiRequest(`${API}/availability`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newSlot),
      });

      if (ok) {
        toast.success("Availability added successfully!");
        setShowAddSlot(false);
        setNewSlot({ date: "", start_time: "", end_time: "" });
        fetchAvailability();
      } else {
        toast.error(data.detail || "Failed to add availability");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  const handleCreateSlotFromDrag = async (slotData) => {
    try {
      const { ok, data } = await apiRequest(`${API}/availability`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(slotData),
      });

      if (ok) {
        toast.success("Availability created!");
        fetchAvailability();
      } else {
        toast.error(data.detail || "Failed to create availability");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      const { ok, data } = await apiRequest(`${API}/availability/${slotId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (ok) {
        toast.success("Availability deleted");
        fetchAvailability();
      } else {
        toast.error(data.detail || "Failed to delete availability");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      const { ok, data } = await apiRequest(`${API}/bookings/${bookingId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });

      if (ok) {
        toast.success(`Booking ${status}!`);
        fetchBookings();
        fetchAvailability();
      } else {
        toast.error(data.detail || "Failed to update booking");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Interpreter Dashboard</h1>
            <p className="text-slate-600 text-sm mt-1" style={{ fontFamily: 'Public Sans' }}>Welcome back, {user?.full_name}</p>
          </div>
          <Button
            data-testid="interpreter-logout-btn"
            onClick={handleLogout}
            variant="ghost"
            className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Profile Card */}
        <div className="card p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Your Profile</h2>
            <Button
              data-testid="edit-profile-btn"
              onClick={() => setShowEditProfile(true)}
              variant="outline"
              className="text-slate-600 hover:text-slate-900"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          </div>
          {user?.approval_status === "pending" && (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-4">
              <p className="text-amber-900 font-medium">Account Pending Approval</p>
              <p className="text-sm text-amber-700 mt-1">Your interpreter account is pending admin approval. You'll be notified once approved.</p>
            </div>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-slate-600 font-medium">Full Name</p>
              <p className="text-slate-900">{user?.full_name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 font-medium">Email</p>
              <p className="text-slate-900">{user?.email}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 font-medium">Languages</p>
              <p className="text-slate-900">{user?.languages?.join(", ") || "Not specified"}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 font-medium">Specializations</p>
              <p className="text-slate-900">{user?.specializations?.join(", ") || "Not specified"}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 font-medium">Court Certifications</p>
              <p className="text-slate-900" data-testid="interpreter-court-certs">{user?.court_certifications?.join(", ") || "None"}</p>
            </div>
            {user?.phone && (
              <div>
                <p className="text-sm text-slate-600 font-medium flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Direct Phone
                </p>
                <p className="text-slate-900" data-testid="interpreter-phone">{user.phone}</p>
              </div>
            )}
          </div>
        </div>

        {/* Edit Profile Dialog */}
        <Dialog open={showEditProfile} onOpenChange={setShowEditProfile}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Your Profile</DialogTitle>
              <DialogDescription>Update your interpreter profile information</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div>
                <Label htmlFor="edit_full_name">Full Name</Label>
                <Input
                  id="edit_full_name"
                  data-testid="edit-fullname-input"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                  className="mt-2"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit_phone">Direct Contact Phone</Label>
                <Input
                  id="edit_phone"
                  data-testid="edit-phone-input"
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  className="mt-2"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <Label htmlFor="edit_languages">Languages (comma-separated)</Label>
                <Input
                  id="edit_languages"
                  data-testid="edit-languages-input"
                  value={profileData.languages}
                  onChange={(e) => setProfileData({ ...profileData, languages: e.target.value })}
                  className="mt-2"
                  placeholder="Spanish, English, French"
                />
              </div>
              <div>
                <Label htmlFor="edit_specializations">Specializations (comma-separated)</Label>
                <Input
                  id="edit_specializations"
                  data-testid="edit-specializations-input"
                  value={profileData.specializations}
                  onChange={(e) => setProfileData({ ...profileData, specializations: e.target.value })}
                  className="mt-2"
                  placeholder="Legal, Medical, Business"
                />
              </div>
              <div>
                <Label htmlFor="edit_court_certifications">Court Certifications (comma-separated)</Label>
                <Input
                  id="edit_court_certifications"
                  data-testid="edit-certifications-input"
                  value={profileData.court_certifications}
                  onChange={(e) => setProfileData({ ...profileData, court_certifications: e.target.value })}
                  className="mt-2"
                  placeholder="Federal Court, State Supreme Court"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditProfile(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="save-profile-btn"
                  type="submit"
                  disabled={editingProfile}
                  className="flex-1 bg-slate-900 text-white hover:bg-slate-800"
                >
                  {editingProfile ? "Saving..." : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Availability Management */}
          <div className="md:col-span-2">
            <div className="card p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>My Availability</h2>
                <Dialog open={showAddSlot} onOpenChange={setShowAddSlot}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-availability-btn" className="bg-slate-900 text-white hover:bg-slate-800">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Slot
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Availability Slot</DialogTitle>
                      <DialogDescription>Set your available time slot for bookings</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleAddSlot} className="space-y-4">
                      <div>
                        <Label htmlFor="date">Date</Label>
                        <Input
                          id="date"
                          data-testid="slot-date-input"
                          type="date"
                          required
                          value={newSlot.date}
                          onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="start_time">Start Time</Label>
                        <Input
                          id="start_time"
                          data-testid="slot-starttime-input"
                          type="time"
                          required
                          value={newSlot.start_time}
                          onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="end_time">End Time</Label>
                        <Input
                          id="end_time"
                          data-testid="slot-endtime-input"
                          type="time"
                          required
                          value={newSlot.end_time}
                          onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                          className="mt-2"
                        />
                      </div>
                      <Button data-testid="slot-submit-btn" type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800">
                        Add Availability
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Tabs defaultValue="calendar" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-6">
                  <TabsTrigger value="calendar" data-testid="calendar-view-tab">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    Calendar
                  </TabsTrigger>
                  <TabsTrigger value="list" data-testid="list-view-tab">
                    <List className="w-4 h-4 mr-2" />
                    List
                  </TabsTrigger>
                  <TabsTrigger value="rules" data-testid="rules-view-tab">
                    <Settings2 className="w-4 h-4 mr-2" />
                    Rules
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="calendar">
                  <CalendarView 
                    slots={availability} 
                    onSlotClick={null}
                    onCreateSlot={handleCreateSlotFromDrag}
                    mode="interpreter"
                  />
                </TabsContent>

                <TabsContent value="list">
                  <div className="space-y-3">
                    {availability.length === 0 ? (
                      <p className="text-slate-600 text-center py-8">No availability slots added yet</p>
                    ) : (
                      availability.map((slot) => (
                        <div key={slot.slot_id} className="border border-slate-200 rounded-lg p-4 flex justify-between items-center hover:border-slate-300 transition-colors">
                          <div className="flex items-center gap-4">
                            <Calendar className="w-5 h-5 text-slate-600" />
                            <div>
                              <p className="font-medium text-slate-900">{slot.date}</p>
                              <p className="text-sm text-slate-600">{formatTimeRange(slot.start_time, slot.end_time)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`status-badge status-${slot.status}`}>
                              {slot.status}
                            </span>
                            {slot.status === "available" && (
                              <Button
                                data-testid={`delete-slot-${slot.slot_id}`}
                                onClick={() => handleDeleteSlot(slot.slot_id)}
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="rules">
                  <EnhancedAvailability 
                    API={API}
                    token={token}
                    userId={user?.user_id}
                    onSlotsGenerated={fetchAvailability}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Booking Requests */}
          <div>
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-6" style={{ fontFamily: 'Manrope' }}>Booking Requests</h2>
              <div className="space-y-3">
                {bookings.length === 0 ? (
                  <p className="text-slate-600 text-center py-8 text-sm">No bookings yet</p>
                ) : (
                  bookings.map((booking) => (
                    <div key={booking.booking_id} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{booking.date}</p>
                          <p className="text-xs text-slate-600">{formatTimeRange(booking.start_time, booking.end_time)}</p>
                        </div>
                        <span className={`status-badge status-${booking.status}`}>
                          {booking.status}
                        </span>
                      </div>
                      {booking.meeting_type && (
                        <p className="text-xs text-slate-700 mb-2">
                          <span className="font-semibold">Meeting Type:</span> <span className="capitalize">{booking.meeting_type.replace('-', ' ')}</span>
                        </p>
                      )}
                      {booking.payment_type && (
                        <p className="text-xs text-slate-700 mb-2">
                          <span className="font-semibold">Payment:</span>{' '}
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            booking.payment_type === 'private-pay' ? 'bg-green-100 text-green-700' :
                            booking.payment_type === 'cja' ? 'bg-blue-100 text-blue-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {booking.payment_type === 'private-pay' ? 'Private Pay' :
                             booking.payment_type === 'cja' ? 'CJA' : 'State Court'}
                          </span>
                        </p>
                      )}
                      {booking.location && (
                        <p className="text-xs text-slate-700 mb-2">
                          <span className="font-semibold">Location:</span> {booking.location}
                        </p>
                      )}
                      {booking.notes && (
                        <p className="text-xs text-slate-600 mb-3">Note: {booking.notes}</p>
                      )}
                      <div className="flex gap-2 mb-2">
                        <Button
                          data-testid={`view-docs-${booking.booking_id}`}
                          onClick={() => setSelectedBookingForDocs(booking)}
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs border-slate-300"
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          Documents
                        </Button>
                      </div>
                      {booking.status === "pending" && (
                        <div className="flex gap-2">
                          <Button
                            data-testid={`confirm-booking-${booking.booking_id}`}
                            onClick={() => handleUpdateBookingStatus(booking.booking_id, "confirmed")}
                            size="sm"
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Confirm
                          </Button>
                          <Button
                            data-testid={`cancel-booking-${booking.booking_id}`}
                            onClick={() => handleUpdateBookingStatus(booking.booking_id, "cancelled")}
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs border-slate-300 hover:bg-slate-50"
                          >
                            <XCircle className="w-3 h-3 mr-1" />
                            Decline
                          </Button>
                        </div>
                      )}
                      {booking.status === "confirmed" && (
                        <Button
                          data-testid={`interpreter-cancel-booking-${booking.booking_id}`}
                          onClick={() => {
                            if (window.confirm("Are you sure you want to cancel this confirmed booking? The user will be notified.")) {
                              handleUpdateBookingStatus(booking.booking_id, "cancelled");
                            }
                          }}
                          size="sm"
                          variant="outline"
                          className="w-full text-xs border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-3 h-3 mr-1" />
                          Cancel Booking
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document Manager */}
      <DocumentManager
        bookingId={selectedBookingForDocs?.booking_id}
        isOpen={!!selectedBookingForDocs}
        onClose={() => setSelectedBookingForDocs(null)}
        bookingDate={selectedBookingForDocs?.date}
      />
    </div>
  );
};

export default InterpreterDashboard;